import 'firebase/compat/firestore';

import { FirestoreMonitor } from './FirestoreMonitor';
import firebase from 'firebase/compat/app';
import { v4 as uuid } from 'uuid'
import { FIRESTORE_WRITE_WHITELISTED_FIELDS } from './whitelist'
export class FirestoreWrapper {
  private static instance: FirestoreWrapper;
  private monitor: FirestoreMonitor;

  private constructor(monitor: FirestoreMonitor) {
    this.monitor = monitor;
  }

  static getInstance(monitor: FirestoreMonitor): FirestoreWrapper {
    if (!FirestoreWrapper.instance) {
      FirestoreWrapper.instance = new FirestoreWrapper(monitor);
    }

    return FirestoreWrapper.instance;
  }

  // Make it static and accept only the data it needs
  private static getCollectionPath(segments: any[] | undefined, path?: string, query?: any): string {
    try {
      // For document references
      if (segments) {
        return segments[0];
      }

      // For path-based references
      if (path) {
        return path.split('/')[0];
      }

      // For queries
      if (query?.path?.segments) {
        return query.path.segments[0];
      }

      return 'unknown';
    } catch (e) {
      console.error('Error extracting collection path:', e);
      return 'unknown';
    }
  }

  initialize() {
    try {
      const firestore = firebase.firestore();
      if (!firestore) return;

      const monitor = this.monitor;

      // Patch DocumentReference.onSnapshot
      const originalDocOnSnapshot = firebase.firestore.DocumentReference.prototype.onSnapshot;
      const originalDocGet = firebase.firestore.DocumentReference.prototype.get;
      // Add new original functions for write operations
      const originalDocSet = firebase.firestore.DocumentReference.prototype.set;
      const originalDocUpdate = firebase.firestore.DocumentReference.prototype.update;

      // Patch Query.onSnapshot
      const originalQueryOnSnapshot = firebase.firestore.Query.prototype.onSnapshot;
      const originalQueryGet = firebase.firestore.Query.prototype.get;

      function getQueryDetails(query: any): { filterFields: string[]; filterDetails: string; orderBy: string; limit: number | null } {
        try {
          // Check if it's a document reference
          if (query._delegate?._key?.path?.segments) {
            const docId = query._delegate._key.path.segments.slice(-1)[0];
            return {
              filterFields: ['id'],
              filterDetails: `id == "${docId}"`,
              orderBy: '',
              limit: null
            };
          }

          // Handle collection queries
          const queryObj = query._delegate?._query || query._query;

          if (!queryObj) {
            return { filterFields: [], filterDetails: '', orderBy: '', limit: null };
          }

          const filterFields: string[] = [];
          const filterDetailsArray: string[] = [];

          // Handle filters from both _t and compositeFilter
          if (queryObj.filters || queryObj._t?.filters) {
            const filters = queryObj.filters || queryObj._t?.filters || [];
            filters.forEach((f: any) => {
              // Handle field path segments
              const fieldPath = f.field?.segments || f.fieldPath?.segments || [];
              const fieldName = fieldPath.join('.');

              // Handle different value types
              let value = f.value;
              if (typeof value === 'object' && value !== null) {
                value = Object.values(value)[0]; // Get the first value from the object
              }

              if (fieldName) {
                filterFields.push(fieldName);
                filterDetailsArray.push(`${fieldName} ${f.op} ${JSON.stringify(value)}`);
              }
            });
          }

          // Handle orderBy from both direct and _t property
          const orderByArray = [];
          const orderByData = queryObj.orderBy || queryObj._t?.orderBy || [];
          for (const order of orderByData) {
            const field = order.field?.segments?.join('.') || order.fieldPath?.segments?.join('.');
            const direction = order.dir || order.direction;
            if (field && direction) {
              orderByArray.push(`${field} ${direction}`);
            }
          }

          // Handle limit from both direct and _t property
          const limit = queryObj.limit || queryObj._t?.limit || null;

          return {
            filterFields,
            filterDetails: filterDetailsArray.join(', '),
            orderBy: orderByArray.join(', '),
            limit
          };
        } catch (e) {
          console.error('Error parsing query details:', e);
          return { filterFields: [], filterDetails: '', orderBy: '', limit: null };
        }
      }

      function filterWhitelistedFields(fields: string[] = [], data: any = {}) {
        const filteredData = {}
        Object.keys(data).forEach((key) => {
          if (fields.includes(key)) {
            filteredData[key] = data[key]
          }
        })
        return filteredData
      }

      // New function to extract write details
      function getWriteDetails(docRef: any, updates: any = {}): { 
        docId: string; 
        fields: string[]; 
        updates: any;
        metadata?: any;
      } {
        try {
          // Extract document ID from the Firestore instance
          const docId = docRef._delegate?._key?.path?.segments?.slice(-1)[0] || 
                        docRef.id || 
                        'unknown';
          
          // Extract document path from the Firestore instance
          const fullPath = docRef._delegate?._key?.path?.segments?.join('/') || 
                           docRef.path || 
                           'unknown';
                    
          const collectionId = docRef.parent?.id || docRef._delegate?.parent?.id
          const filteredUpdates = filterWhitelistedFields(FIRESTORE_WRITE_WHITELISTED_FIELDS[collectionId], updates);
          const filteredFields = Object.keys(filteredUpdates);
          
          // Extract additional metadata from the document reference
          const docMetadata = {
            documentPath: fullPath,
            collectionId: docRef.parent?.id || docRef._delegate?.parent?.id || 'unknown',
            firestoreVersion: firebase.SDK_VERSION || 'unknown',
            exists: docRef._delegate?._document ? true : false,
            // Extract more information if available
            createTime: docRef._delegate?._document?.createTime?.toString(),
            updateTime: docRef._delegate?._document?.updateTime?.toString()
          };

          filteredUpdates.docId = docId;

          const filterDetails = Object.keys(filteredUpdates).map((key) => `${key} = ${JSON.stringify(filteredUpdates[key])}`).join(', ');

          return {
            docId,
            fields: filteredFields,
            metadata: docMetadata,
            filterDetails
          };
        } catch (e) {
          console.error('Error extracting write details:', e);
          return { 
            docId: 'unknown',
            fields: [],
            updates: {},
            metadata: {}
          };
        }
      }

      // Override Document methods
      firebase.firestore.DocumentReference.prototype.onSnapshot = function(...args) {
        const unsubscribe = originalDocOnSnapshot.apply(this, args);

        try {
          const queryStartTime = Date.now();
          const id = uuid();
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          const queryDetails = getQueryDetails(this);

          // Track the listener addition
          monitor.afterListenerAdded({
            id,
            collection: collectionPath,
            timestamp: Date.now(),
            queryDetails
          });

          // Return a wrapped unsubscribe function that tracks listener removal
          return () => {
            try {
              monitor.afterListenerRemoved({
                id,
                collection: collectionPath,
                timestamp: Date.now(),
                queryDetails
              });
            } catch (error) {
              console.warn('FirestoreWrapper: Error in listener removal tracking:', error);
            }
            return unsubscribe();
          };
        } catch (error) {
          console.warn('FirestoreWrapper: Error in onSnapshot tracking:', error);
          return unsubscribe;
        }
      };

      firebase.firestore.DocumentReference.prototype.get = async function(...args) {
        let endTracking = () => {};
        try {
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          const queryDetails = getQueryDetails(this);
          const startTime = Date.now();

          endTracking = monitor.trackQuery({
            collection: collectionPath,
            queryType: 'document',
            type: 'get',
            timestamp: startTime,
            queryDetails
          });
        } catch (error) {
          console.warn('FirestoreWrapper: Error in get tracking setup:', error);
        }

        try {
          const snapshot = await originalDocGet.apply(this, args);
          try {
            endTracking({
              resultCount: snapshot.exists ? 1 : 0
            });
          } catch (error) {
            console.warn('FirestoreWrapper: Error in get completion tracking:', error);
          }
          return snapshot;
        } catch (error) {
          try {
            endTracking({ error: error.message });
          } catch (trackingError) {
            console.warn('FirestoreWrapper: Error in get error tracking:', trackingError);
          }
          throw error;
        }
      };

      // Track document set operations
      firebase.firestore.DocumentReference.prototype.set = async function(...args) {
        let endTracking = () => {};
        try {
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          const writeDetails = getWriteDetails(this, args[0]);
          const startTime = Date.now();
          
          endTracking = monitor.trackQuery({
            collection: collectionPath,
            queryType: 'write',
            type: 'set',
            timestamp: startTime,
            queryDetails: { filterDetails:  writeDetails.filterDetails, filterFields: writeDetails.fields, metadata: writeDetails.metadata }
          });
        } catch (error) {
          console.warn('FirestoreWrapper: Error in set tracking setup:', error);
        }

        try {
          const result = await originalDocSet.apply(this, arguments);
          try {
            endTracking({
              resultCount: 1, // Set operations always affect 1 document
              success: true
            });
          } catch (error) {
            console.warn('FirestoreWrapper: Error in set completion tracking:', error);
          }
          return result;
        } catch (error) {
          try {
            endTracking({ 
              error: error.message,
              success: false
            });
          } catch (trackingError) {
            console.warn('FirestoreWrapper: Error in set error tracking:', trackingError);
          }
          throw error;
        }
      };

      // Track document update operations
      firebase.firestore.DocumentReference.prototype.update = async function(...args) {
        let endTracking = () => {};
        try {
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          
          
          const writeDetails = getWriteDetails(this, args[0]);
          const startTime = Date.now();
      
          endTracking = monitor.trackQuery({
            collection: collectionPath,
            queryType: 'write',
            type: 'update',
            timestamp: startTime,
            queryDetails: { filterDetails:  writeDetails.filterDetails, filterFields: writeDetails.fields, metadata: writeDetails.metadata }
          });
        } catch (error) {
          console.warn('FirestoreWrapper: Error in update tracking setup:', error);
        }

        try {
          const result = await originalDocUpdate.apply(this, args);
          try {
            endTracking({
              resultCount: 1, // Update operations always affect 1 document
              success: true
            });
          } catch (error) {
            console.warn('FirestoreWrapper: Error in update completion tracking:', error);
          }
          return result;
        } catch (error) {
          try {
            endTracking({ 
              error: error.message,
              success: false 
            });
          } catch (trackingError) {
            console.warn('FirestoreWrapper: Error in update error tracking:', trackingError);
          }
          throw error;
        }
      };

      // Override Query methods
      firebase.firestore.Query.prototype.onSnapshot = function(...args) {
        const unsubscribe = originalQueryOnSnapshot.apply(this, args);

        try {
          const queryStartTime = Date.now();
          const id = uuid();
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          const queryDetails = getQueryDetails(this);

          // Track the listener addition
          monitor.afterListenerAdded({
            id,
            collection: collectionPath,
            timestamp: Date.now(),
            queryDetails
          });

          return () => {
            try {
              monitor.afterListenerRemoved({
                id,
                collection: collectionPath,
                timestamp: Date.now(),
                queryDetails
              });
            } catch (error) {
              console.warn('FirestoreWrapper: Error in query listener removal tracking:', error);
            }
            return unsubscribe();
          };
        } catch (error) {
          console.warn('FirestoreWrapper: Error in query onSnapshot tracking:', error);
          return unsubscribe;
        }
      };

      firebase.firestore.Query.prototype.get = async function(...args) {
        let endTracking = () => {};
        try {
          const collectionPath = FirestoreWrapper.getCollectionPath(
            this._delegate?._key?.path?.segments,
            this.path,
            this._delegate?._query
          );
          const queryDetails = getQueryDetails(this);
          const startTime = Date.now();

          endTracking = monitor.trackQuery({
            collection: collectionPath,
            queryType: 'query',
            type: 'get',
            timestamp: startTime,
            queryDetails
          });
        } catch (error) {
          console.warn('FirestoreWrapper: Error in query get tracking setup:', error);
        }

        try {
          const snapshot = await originalQueryGet.apply(this, args);
          try {
            endTracking({
              resultCount: snapshot.docs.length
            });
          } catch (error) {
            console.warn('FirestoreWrapper: Error in query get completion tracking:', error);
          }
          return snapshot;
        } catch (error) {
          try {
            endTracking({ error: error.message });
          } catch (trackingError) {
            console.warn('FirestoreWrapper: Error in query get error tracking:', trackingError);
          }
          throw error;
        }
      };
    } catch (e) {
      console.error('FirestoreWrapper: Error during initialization:', e);
    }
  }
}
